import React from 'react';
import { Button, FormError, FormInput, FormSelect, Tooltip } from '@getro/rombo';
import { Formik, Form, FastField, Field, FieldArray } from 'formik';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import * as yup from 'yup';
import { ArrayFieldOption } from './arrayFieldOption';

export const CustomFieldForm = ({ customField, isLoading, onSubmit, onCancel, page }) => {
  const formSchema = yup.object().shape({
    name: yup.string().required('Field Name is required'),
    type: yup
      .mixed()
      .required('Field Type is required')
      .test('is-valid-type', 'Please select a valid type', (value) => {
        if (!value) return false;
        return value && typeof value === 'object' && 'value' in value;
      }),
    scope: yup
      .mixed()
      .required('Field Scope is required')
      .test('is-valid-scope', 'Please select a valid scope', (value) => {
        if (!value) return false;
        return value && typeof value === 'object' && 'value' in value;
      }),
    options: yup.array().when('type', {
      is: (t) => t && t.value === 'single_select',
      then: (schema) =>
        schema
          .min(1, 'Add at least one option')
          .required('Add at least one option')
          .of(
            yup.object().shape({
              label: yup
                .string()
                .transform((v) => (typeof v === 'string' ? v.trim() : v))
                .min(1, 'Option cannot be empty')
                .required('Option cannot be empty'),
              id: yup.mixed().nullable(),
            }),
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const validate = (values) => {
    const errors = {};

    if (values.type?.value === 'single_select' && values.options) {
      if (!values.options.length) {
        errors.options = ['empty-options'];
        return errors;
      }

      const trimmedOptions = values.options.map((option) => option?.label && option.label.trim()).filter(Boolean);
      if (trimmedOptions.length > 0) {
        const uniqueOptions = new Set(trimmedOptions);
        if (uniqueOptions.size !== trimmedOptions.length) {
          errors.options = ['Each option must be unique, make sure there are no duplicates.'];
        }
      }
    }

    return errors;
  };
  const customFieldsKindOptions = [
    { label: 'Text', value: 'text' },
    { label: 'Single select', value: 'single_select' },
  ];

  const scopeOptions = [
    { label: 'Global', value: 'global', description: 'Available in the Contacts view and all lists.' },
    { label: 'List-specific', value: 'list-specific', description: 'Available only in this list.' },
  ];

  const getDefaultScope = () => {
    if (customField?.id) {
      return customField.sharedList?.id
        ? scopeOptions.find((o) => o.value === 'list-specific')
        : scopeOptions.find((o) => o.value === 'global');
    }

    return page === 'all_contacts'
      ? scopeOptions.find((o) => o.value === 'global')
      : scopeOptions.find((o) => o.value === 'list-specific');
  };

  const initialValues = {
    name: customField.name,
    type: customFieldsKindOptions.find((option) => option.value === customField.type) || customFieldsKindOptions[1],
    scope: getDefaultScope(),
    options: customField.params?.options || [{ label: '', id: null }],
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={validate}
      validationSchema={formSchema}
      enableReinitialize
    >
      {({ values }) => {
        const isSingle = values.type?.value === 'single_select';

        return (
          <Form>
            <Flex mt="16px" flexDirection="column" sx={{ gap: '16px', '& span': { display: 'inline-block' } }}>
              <FastField
                id="name"
                name="name"
                component={FormInput}
                label="Name"
                size="small"
                autoFocus={!customField?.id}
              />
              <Box onClick={(e) => e.stopPropagation()} sx={{ position: 'relative' }}>
                <Field
                  name="type"
                  component={FormSelect}
                  label="Type"
                  size="small"
                  disabled={!!customField?.id}
                  options={customFieldsKindOptions}
                />
                {!!customField?.id && (
                  <Tooltip
                    position="right-end"
                    options={{
                      delayShow: 500,
                    }}
                    trigger={<Box width="100%" height="100%" sx={{ position: 'absolute', top: 0, right: 0 }} />}
                    content="This cannot be edited after the field is created"
                    sx={{
                      zIndex: 100000,
                    }}
                  />
                )}
              </Box>
              {isSingle && (
                <FieldArray name="options">
                  {({ push, remove }) => (
                    <Box>
                      <Text fontSize="14px" fontWeight="medium" mb="8px" color="text.dark">
                        Options
                      </Text>
                      <Flex flexDirection="column" sx={{ gap: '8px' }}>
                        {values.options.map((opt, idx) => (
                          <ArrayFieldOption
                            // eslint-disable-next-line react/no-array-index-key
                            key={`single-select-option-${idx}`}
                            index={idx}
                            push={push}
                            remove={remove}
                            isEditMode={!!customField?.id}
                          />
                        ))}

                        <Button
                          type="button"
                          variant="tertiary"
                          size="small"
                          onClick={() => push({ label: '', id: null })}
                          sx={{ alignSelf: 'flex-start', px: '4px' }}
                        >
                          + Add option
                        </Button>

                        <Field name="options">
                          {({ form }) => {
                            const arrayError = form.errors?.options;
                            const arrayTouched = form.touched?.options;

                            if (arrayError && arrayTouched && Array.isArray(arrayError) && arrayError.length > 0) {
                              // Check for custom validation errors (array-level errors are strings)
                              const stringErrors = arrayError.filter((err) => typeof err === 'string');
                              if (stringErrors.length > 0) {
                                if (stringErrors.includes('empty-options')) {
                                  return <FormError mt="0px">Add at least one option</FormError>;
                                }
                                // Show duplicate error or other array-level errors
                                return <FormError mt="0px">{stringErrors[0]}</FormError>;
                              }
                            }
                            return null;
                          }}
                        </Field>
                      </Flex>
                    </Box>
                  )}
                </FieldArray>
              )}
              <Box onClick={(e) => e.stopPropagation()} sx={{ position: 'relative' }}>
                <Field
                  name="scope"
                  component={FormSelect}
                  label="Scope"
                  size="small"
                  disabled={!!customField?.id || page === 'all_contacts'}
                  options={scopeOptions}
                />
                {(!!customField?.id || page === 'all_contacts') && (
                  <Tooltip
                    position="right-end"
                    trigger={<Box width="100%" height="100%" sx={{ position: 'absolute', top: 0, right: 0 }} />}
                    content={
                      page === 'all_contacts'
                        ? 'Custom fields can be global or list-specific. You can create list-specific custom fields from a list.'
                        : 'This cannot be edited after the field is created'
                    }
                    sx={{
                      zIndex: 100000,
                    }}
                  />
                )}
              </Box>
              <Flex alignItems="center" sx={{ gap: '8px' }}>
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={onCancel}
                  sx={{ flex: 1, justifyContent: 'center' }}
                >
                  Cancel
                </Button>
                <Button type="submit" size="small" loading={isLoading} sx={{ flex: 1, justifyContent: 'center' }}>
                  {customField.id ? 'Update field' : 'Create field'}
                </Button>
              </Flex>
            </Flex>
          </Form>
        );
      }}
    </Formik>
  );
};

CustomFieldForm.propTypes = {
  customField: PropTypes.object,
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  page: PropTypes.string,
};

CustomFieldForm.defaultProps = {
  customField: {},
  isLoading: false,
  page: null,
};
